import { useEffect } from "react";
import type { HierarchyNode } from "d3-hierarchy";
import { TradeDirection } from "@atlas-int/data/types";
import type { EnrichedTradeDataItem } from "@atlas-int/data/types";
import { UIView } from "@atlas-int/data";

/** A hierarchy leaf node with data */
type TreemapLeafNode = HierarchyNode<EnrichedTradeDataItem>;

/** Minimal product metadata fields needed for download sector lookup */
interface ProductMetadataForDownload {
  productId?: string;
  nameShortEn?: string | null;
}

interface UseDownloadDataProps {
  filteredServicesRootLeaves: TreemapLeafNode[] | undefined;
  filteredNonServicesRootLeaves: TreemapLeafNode[] | undefined;
  filteredRootLeaves: TreemapLeafNode[] | undefined;
  view: UIView;
  tradeDirection: string;
  totalSum: number | undefined;
  products?: ProductMetadataForDownload[];
  dataRef: React.MutableRefObject<Record<string, unknown>[]>;
}

export const useDownloadData = ({
  filteredServicesRootLeaves,
  filteredNonServicesRootLeaves,
  filteredRootLeaves,
  view,
  tradeDirection,
  totalSum,
  products,
  dataRef,
}: UseDownloadDataProps): void => {
  useEffect(() => {
    if (view === UIView.Products) {
      const leavesForDownload =
        filteredServicesRootLeaves?.length || filteredNonServicesRootLeaves?.length
          ? [
              ...(filteredServicesRootLeaves || []),
              ...(filteredNonServicesRootLeaves || []),
            ]
          : filteredRootLeaves || [];

      if (leavesForDownload.length) {
        dataRef.current = leavesForDownload.map((d) => {
          const value =
            tradeDirection === TradeDirection.Exports
              ? d.data.exportValue
              : d.data.importValue;
          return {
            Name: d.data.nameEn,
            [`Gross ${tradeDirection === TradeDirection.Exports ? "Export" : "Import"}`]:
              value,
            Code: d.data.productCode,
            Sector: products?.find((p) => p.productId === d.data.sectorId)
              ?.nameShortEn ?? undefined,
            Share: totalSum ? (value / totalSum) * 100 : 0,
          };
        });
      } else {
        dataRef.current = [];
      }
    } else if (view === UIView.Markets) {
      if (filteredRootLeaves?.length) {
        dataRef.current = [...filteredRootLeaves].map((d) => {
          const value =
            tradeDirection === TradeDirection.Exports
              ? d.data.exportValue
              : d.data.importValue;
          return {
            Name: d.data.nameEn,
            [`Gross ${tradeDirection === TradeDirection.Exports ? "Export" : "Import"}`]:
              value,
            Share: totalSum ? (value / totalSum) * 100 : 0,
          };
        });
      } else {
        dataRef.current = [];
      }
    }
  }, [
    filteredServicesRootLeaves,
    filteredNonServicesRootLeaves,
    dataRef,
    tradeDirection,
    products,
    view,
    filteredRootLeaves,
    totalSum,
  ]);
};
